import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// The homepage story beats, anchored to the sections the cinematic camera
// passes through. The 5-step mortgage journey (DISCOVER…CLOSE) lives inside
// the Process section; the rail marks the three acts of the homepage.
const CHAPTERS = [
  { id: "dream", label: "DREAM", target: "#home" },
  { id: "proof", label: "PROOF", target: "#recently-funded" },
  { id: "journey", label: "THE JOURNEY", target: "#process" },
  { id: "home", label: "HOME", target: '[data-testid="final-cta-section"]' },
];

export default function JourneyRail() {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    const mq = window.matchMedia("(min-width: 1536px)");
    const check = () => setEnabled(mq.matches);
    check();
    mq.addEventListener("change", check);
    return () => mq.removeEventListener("change", check);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const mid = window.scrollY + window.innerHeight * 0.5;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(window.scrollY / max, 1) : 0);
      // Pick the first chapter whose section CONTAINS the mid-viewport line
      // (#calculator is nested inside the hero, so containment — not "last top
      // above mid" — keeps DREAM lit at the top of the page). Fallback: the
      // last chapter already passed.
      let idx = 0;
      for (let i = 0; i < CHAPTERS.length; i++) {
        const el = document.querySelector(CHAPTERS[i].target);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + window.scrollY;
        const bottom = top + el.offsetHeight;
        if (top <= mid && mid < bottom) {
          idx = i;
          break;
        }
        if (top <= mid) idx = i;
      }
      setActive(idx);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [enabled]);

  // Fade the rail away when the footer is in view so it never overlaps it
  useEffect(() => {
    if (!enabled) return;
    const footer = document.querySelector('[data-testid="site-footer"]');
    if (!footer) return;
    const obs = new IntersectionObserver(([entry]) => setFooterVisible(entry.isIntersecting));
    obs.observe(footer);
    return () => obs.disconnect();
  }, [enabled]);

  if (!enabled) return null;
  const go = (target) => {
    const el = document.querySelector(target);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -60, duration: 1.6 });
    else el.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });
  };

  return (
    <motion.nav
      data-testid="journey-rail"
      aria-label="Journey"
      initial={reduced ? false : { opacity: 0, x: -10 }}
      animate={{ opacity: footerVisible ? 0 : 1, x: 0 }}
      transition={
        entered
          ? { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
          : { duration: 1.2, delay: 1.4, ease: [0.22, 1, 0.36, 1] }
      }
      onAnimationComplete={() => setEntered(true)}
      className={`fixed left-7 top-1/2 -translate-y-1/2 z-30 flex flex-col ${
        footerVisible ? "pointer-events-none" : ""
      }`}
    >
      <div className="absolute left-[3px] top-2 bottom-2 w-px bg-white/10" aria-hidden="true">
        <div
          data-testid="journey-rail-progress"
          className="w-full bg-gradient-to-b from-gold/70 to-gold origin-top transition-transform duration-700 ease-out"
          style={{ height: "100%", transform: `scaleY(${progress})` }}
        />
      </div>
      {CHAPTERS.map((c, i) => {
        const isActive = i === active;
        return (
          <button
            key={c.id}
            data-testid={`journey-chapter-${c.id}`}
            onClick={() => go(c.target)}
            aria-label={`Go to ${c.label}`}
            aria-current={isActive ? "true" : undefined}
            className="group relative flex items-center py-[13px] pl-0 pr-2 text-left"
          >
            <span
              className={`w-[7px] h-[7px] rotate-45 border transition-all duration-500 ${
                isActive ? "bg-gold border-gold scale-110" : "bg-transparent border-white/25 group-hover:border-gold/60"
              }`}
            />
            <span
              className={`absolute left-6 whitespace-nowrap text-[10px] tracking-[0.32em] font-medium transition-all duration-500 ${
                isActive
                  ? "text-gold opacity-100 translate-x-0"
                  : "text-ivory/35 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-ivory/80"
              }`}
            >
              {c.label}
            </span>
          </button>
        );
      })}
    </motion.nav>
  );
}
